import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/content";

const SECTION_IDS = NAV_LINKS
  .filter((link) => link.href.startsWith("/#"))
  .map((link) => link.id);

export const FloatingRibbonNav = ({ pathname = "/", ready = true }) => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      if (pathname === "/digital-studio") return;

      let current = "";
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom >= 140) {
            current = id;
            break;
          }
        }
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const activeId =
    pathname === "/digital-studio" ? "digital-studio" : active;

  return (
    <div
      data-testid="floating-ribbon-nav"
      className="floating-ribbon-shell fixed inset-x-0 z-[90] mx-auto w-[calc(100%-1rem)] max-w-6xl sm:w-[calc(100%-1.5rem)]"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
        opacity: ready ? 1 : 0,
        transition: "opacity 0.8s ease",
      }}
    >
      <nav
        className="floating-ribbon-inner flex items-center justify-between rounded-full px-2 py-2 sm:px-4 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
        style={{
          backgroundColor: scrolled
            ? "rgba(11,10,8,0.86)"
            : "rgba(11,10,8,0.62)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid var(--border-c)",
          boxShadow: "var(--shadow-ribbon)",
          transition: "background-color 0.4s ease",
        }}
      >
        {/* Logo */}
        <a
          data-testid="nav-logo"
          href="/"
          className="nav-logo-button flex items-baseline gap-2 pl-2 pr-1 lg:justify-self-start"
          aria-label="Forte by Ryann home"
        >
          <span
            className="font-serif"
            style={{
              color: "var(--text)",
              fontSize: "1.35rem",
              letterSpacing: "0.14em",
              lineHeight: 1,
            }}
          >
            FORTE
          </span>
          <span
            className="font-serif hidden sm:inline"
            style={{
              color: "var(--muted)",
              fontStyle: "italic",
              fontSize: "0.78rem",
              letterSpacing: "0.12em",
            }}
          >
            By Ryann
          </span>
        </a>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 lg:flex lg:justify-self-center">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              href={link.href}
              className="relative px-3 py-2"
              style={{
                fontFamily: "var(--font-sans)",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                fontSize: "11px",
                fontWeight: 600,
                color: activeId === link.id ? "var(--text)" : "var(--muted)",
                transition: "color 0.25s ease",
              }}
            >
              {link.id === "digital-studio" ? (
                <>
                  <span className="hidden xl:inline">Digital Studio</span>
                  <span className="xl:hidden">Studio</span>
                </>
              ) : (
                link.label
              )}
              {activeId === link.id && (
                <span
                  style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 2,
                    transform: "translateX(-50%)",
                    width: 16,
                    height: 2,
                    borderRadius: 2,
                    backgroundColor: "var(--bronze)",
                  }}
                />
              )}
            </a>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex items-center gap-2 lg:justify-self-end">
          <a
            data-testid="nav-inquire-button"
            href="/#contact"
            className="btn-fx btn-ghost nav-inquire-cta"
          >
            Inquire
          </a>
          <a
            data-testid="nav-book-class-button"
            href="/digital-studio#live"
            className="btn-fx btn-primary nav-book-cta"
          >
            Book a Class
          </a>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="nav-mobile-trigger"
                className="btn-fx btn-ghost nav-menu-trigger lg:hidden"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="nav-mobile-sheet"
              className="mobile-nav-sheet"
              style={{
                backgroundColor: "var(--bg-2)",
                borderLeft: "1px solid var(--border-c)",
                color: "var(--text)",
              }}
            >
              <SheetTitle className="sr-only">
                Forte by Ryann navigation
              </SheetTitle>
              <SheetDescription className="sr-only">
                Navigate the Forte by Ryann website or explore the Digital Studio.
              </SheetDescription>
              <div className="mt-10 flex flex-col gap-1">
                <span
                  className="font-serif"
                  style={{
                    fontSize: "1.5rem",
                    letterSpacing: "0.12em",
                    marginBottom: "1.2rem",
                  }}
                >
                  FORTE{" "}
                  <span
                    style={{
                      color: "var(--muted)",
                      fontStyle: "italic",
                      fontSize: "0.9rem",
                    }}
                  >
                    By Ryann
                  </span>
                </span>
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.id}>
                    <a
                      data-testid={`nav-mobile-link-${link.id}`}
                      href={link.href}
                      className="py-3 text-left"
                      style={{
                        fontFamily: "var(--font-sans)",
                        textTransform: "uppercase",
                        letterSpacing: "0.2em",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "var(--text-2)",
                        borderBottom: "1px solid var(--border-c)",
                      }}
                    >
                      {link.label}
                    </a>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <a
                    href="/#contact"
                    className="btn-fx btn-outline mt-6 w-full"
                  >
                    Inquire
                  </a>
                </SheetClose>
                <SheetClose asChild>
                  <a
                    href="/digital-studio#live"
                    className="btn-fx btn-primary mt-2 w-full"
                  >
                    Book a Class
                  </a>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
};
